import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { State } from 'src/types';
import { StatusFilterOption } from '../hooks/useCategoryTableData';

const ALL_STATUS_OPTIONS: {
  label: string;
  value: NonNullable<StatusFilterOption>;
}[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Form required', value: 'Form required' },
  { label: 'Inactive', value: 'Inactive' },
];

const DEBOUNCE_MS = 300;

interface CategoryTableToolbarProps {
  onSearch: (term: string) => void;
  statusFilter: StatusFilterOption;
  onStatusChange: (status: StatusFilterOption) => void;
  isCOI?: boolean;
}

export const CategoryTableToolbar: React.FC<CategoryTableToolbarProps> = ({
  onSearch,
  statusFilter,
  onStatusChange,
  isCOI = false,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const isLegacyMode = useSelector(
    (state: State) => state.organisation?.isLegacyMode,
  );
  const STATUS_OPTIONS = isLegacyMode
    ? ALL_STATUS_OPTIONS.filter((o) => o.value !== 'Form required')
    : ALL_STATUS_OPTIONS;
  const [value, setValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onSearch(newValue), DEBOUNCE_MS);
    },
    [onSearch],
  );

  const handleStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: StatusFilterOption,
  ) => {
    onStatusChange(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: tokens.core.spacing[3].value,
      }}
      data-testid="category-table-toolbar"
    >
      <Box>
        <Typography
          variant="body2"
          component="label"
          htmlFor="category-search-input"
          sx={{
            display: 'block',
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
            color: tokens.semantic.color.type.default.value,
            marginBottom: '4px',
          }}
        >
          Search
        </Typography>
        <TextField
          id="category-search-input"
          variant="outlined"
          placeholder={
            isCOI
              ? 'Search by Category name and Created by'
              : 'Search by Category name, Type and Created by'
          }
          value={value}
          onChange={handleChange}
          data-testid="category-search-input"
          sx={{ width: '420px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i className="uil uil-search" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <ToggleButtonGroup
        value={statusFilter ?? null}
        exclusive
        onChange={handleStatusChange}
        size="small"
        data-testid="category-status-filter"
        sx={{
          '& .MuiToggleButtonGroup-grouped': {
            border: `1px solid ${tokens.semantic.color.type.default.value}`,
            textTransform: 'none',
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
            padding: '4px 12px',
            whiteSpace: 'nowrap',
            color: tokens.semantic.color.type.default.value,
            '&:not(:first-of-type)': {
              marginLeft: 0,
            },
            '&:not(:last-of-type)': {
              borderRight: 0,
            },
            '&:first-of-type': {
              borderRadius: '8px 0 0 8px',
            },
            '&:last-of-type': {
              borderRadius: '0 8px 8px 0',
            },
            '&.Mui-selected': {
              backgroundColor: tokens.semantic.color.type.default.value,
              color: tokens.semantic.color.action.primary.onPrimary.value,
              '&:hover': {
                backgroundColor: tokens.semantic.color.type.default.value,
              },
            },
          },
        }}
      >
        {STATUS_OPTIONS.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            data-testid={`category-status-option-${option.value}`}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
